import { useEffect, useMemo, useRef } from 'react';
import { goalDataTypeEnums } from 'utils';

const MAX_VISIBLE = 8;

export const TrialCircles = ({ current, item }) => {
    const activeRef = useRef(null);
    const wrapperRef = useRef(null);
    const trials = item?.tSummary?.trials || [];

    const extendedTrials = useMemo(() => [...trials, { id: 'gray' }], [trials]);

    const activeStep = current || { id: 'gray' };

    const currentIndex = useMemo(() => {
        const idx = extendedTrials.findIndex((t) => t?.id === activeStep?.id);
        return idx >= 0 ? idx : extendedTrials.length - 1;
    }, [extendedTrials, activeStep]);

    const visibleTrials = useMemo(() => {
        const total = extendedTrials.length;
        if (total <= MAX_VISIBLE) return extendedTrials;

        const half = Math.floor(MAX_VISIBLE / 2);
        let start = currentIndex - half;
        let end = start + MAX_VISIBLE;

        if (start < 0) {
            start = 0;
            end = MAX_VISIBLE;
        }

        if (end > total) {
            end = total;
            start = end - MAX_VISIBLE;
        }

        return extendedTrials.slice(start, end);
    }, [extendedTrials, currentIndex]);

    const renderCircleColor = (trial, goal) => {
        if (trial.id === 'gray') return '#E5E7EB';
        switch (goal?.tSummary?.dataType) {
            case goalDataTypeEnums.PERCENT:
                return trial?.value === 1 ? '#038A4D' : '#FE7070';
            case goalDataTypeEnums.FREQUENCY:
                return '#1668E8';
            case goalDataTypeEnums.WHOLE_INTERVAL:
            case goalDataTypeEnums.PARTIAL_INTERVAL:
            case goalDataTypeEnums.MOMENTARY_TIME_SAMPLING:
                return trial?.value === 1 ? '#1668E8' : '#FE7070';
            default:
                return '#1668E8';
        }
    };

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const active = activeRef.current;
        if (wrapper && active) {
            const wrapperRect = wrapper.getBoundingClientRect();
            const activeRect = active.getBoundingClientRect();
            wrapper.scrollTo({
                left: wrapper.scrollLeft + (activeRect.left - wrapperRect.left) - (wrapper.clientWidth - activeRect.width) / 2,
                behavior: 'smooth',
            });
        }
    }, [currentIndex]);

    return (
        <div className="trial-indicators-wrapper" ref={wrapperRef}>
            {visibleTrials.map((trial, index) => {
                const isActive = trial?.id === activeStep?.id;
                return (
                    <span key={trial?.id || index} ref={isActive ? activeRef : null} className="circle-wrapper">
                        <span
                            className="circle-dot"
                            style={{
                                backgroundColor: renderCircleColor(trial, item),
                                transform: isActive ? 'scale(1.6)' : 'scale(1)',
                            }}
                        />
                    </span>
                );
            })}
        </div>
    );
};
